'use client';

import { c as _c } from "react/compiler-runtime";
import IconArrowRightWhite from '@/components/assets/icon_arrow_right_white.svg';
import { useCallback, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
type CarouselAlignment = 'left' | 'center' | 'right';
type LogoOverlayRecommendationCarouselProps = {
  children: ReactNode;
  /** Controlled by parent via UA + route context. */
  showArrows?: boolean;
  /** Initial visible region of the scrollable list, derived from logo position. */
  alignment?: CarouselAlignment;
};
export default function LogoOverlayRecommendationCarousel(t0) {
  const $ = _c(20);
  const {
    children,
    showArrows: t1,
    alignment: t2
  } = t0;
  const showArrows = t1 === undefined ? true : t1;
  const alignment = t2 === undefined ? "left" : t2;
  const scrollRef = useRef(null);
  const contentRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  let t3;
  if ($[0] !== alignment) {
    t3 = () => {
      const el = scrollRef.current;
      if (!el) {
        return;
      }
      const maxScrollLeft = Math.max(0, el.scrollWidth - el.clientWidth);
      if (alignment === "right") {
        el.scrollLeft = maxScrollLeft;
        return;
      }
      if (alignment === "center") {
        el.scrollLeft = maxScrollLeft / 2;
        return;
      }
      el.scrollLeft = 0;
    };
    $[0] = alignment;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const alignScrollPosition = t3;
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      const el_0 = scrollRef.current;
      if (!el_0) {
        return;
      }
      setCanScrollLeft(el_0.scrollLeft > 0);
      setCanScrollRight(el_0.scrollLeft < el_0.scrollWidth - el_0.clientWidth - 1);
    };
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const updateArrows = t4;
  let t5;
  let t6;
  if ($[3] !== alignScrollPosition) {
    t5 = () => {
      const el_1 = scrollRef.current;
      if (!el_1) {
        return;
      }
      const syncLayout = () => {
        alignScrollPosition();
        updateArrows();
      };
      syncLayout();
      const frameId = requestAnimationFrame(syncLayout);
      el_1.addEventListener("scroll", updateArrows, {
        passive: true
      });
      const ro = new ResizeObserver(syncLayout);
      ro.observe(el_1);
      if (contentRef.current) {
        ro.observe(contentRef.current);
      }
      return () => {
        cancelAnimationFrame(frameId);
        el_1.removeEventListener("scroll", updateArrows);
        ro.disconnect();
      };
    };
    t6 = [alignScrollPosition, updateArrows];
    $[3] = alignScrollPosition;
    $[4] = t5;
    $[5] = t6;
  } else {
    t5 = $[4];
    t6 = $[5];
  }
  useLayoutEffect(t5, t6);
  let t7;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = direction => {
      const el_2 = scrollRef.current;
      if (!el_2) {
        return;
      }
      const amount = el_2.clientWidth * 0.8;
      el_2.scrollBy({
        left: direction === "left" ? -amount : amount,
        behavior: "smooth"
      });
    };
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  const scrollBy = t7;
  let t8;
  if ($[7] !== canScrollLeft || $[8] !== showArrows) {
    t8 = showArrows && canScrollLeft && <button type="button" onClick={() => scrollBy("left")} className="absolute top-1/2 left-2 z-interactive flex h-[30px] w-[30px] -translate-y-1/2 items-center justify-center rounded-full bg-[rgba(36,36,36,0.7)] backdrop-blur-xs transition-[background-color,transform] duration-150 hover:scale-105 hover:bg-[rgba(36,36,36,0.9)]" aria-label="Scroll left"><IconArrowRightWhite className="rotate-180" /></button>;
    $[7] = canScrollLeft;
    $[8] = showArrows;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      "--overlay-card-w": "clamp(96px, 10.56vw, 152px)"
    };
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] !== children) {
    t10 = <div ref={scrollRef} className="no-scrollbar w-fit max-w-full overflow-x-auto overflow-y-hidden p-2" style={t9 as React.CSSProperties}><div ref={contentRef} className="flex w-fit items-start gap-2">{children}</div></div>;
    $[11] = children;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== canScrollRight || $[14] !== showArrows) {
    t11 = showArrows && canScrollRight && <button type="button" onClick={() => scrollBy("right")} className="absolute top-1/2 right-2 z-interactive flex h-[30px] w-[30px] -translate-y-1/2 items-center justify-center rounded-full bg-[rgba(36,36,36,0.7)] backdrop-blur-xs transition-[background-color,transform] duration-150 hover:scale-105 hover:bg-[rgba(36,36,36,0.9)]" aria-label="Scroll right"><IconArrowRightWhite /></button>;
    $[13] = canScrollRight;
    $[14] = showArrows;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== t10 || $[17] !== t11 || $[18] !== t8) {
    t12 = <div className="relative w-fit max-w-full">{t8}{t10}{t11}</div>;
    $[16] = t10;
    $[17] = t11;
    $[18] = t8;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  return t12;
}